import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, MessageCircleOff } from "lucide-react";
import { api } from "@/lib/api";

/**
 * useWhatsAppCheck — hook que valida se um número tem WhatsApp via Z-API.
 *
 * Estados:
 *   idle            → nada (input curto ou vazio)
 *   loading         → esperando resposta
 *   has             → tem WhatsApp (check verde)
 *   no              → não tem (X vermelho + aviso)
 *   not_configured  → empresa sem Z-API (UI oculta ícones)
 *   error           → falha de rede/API (não bloqueia UX)
 *
 * Debounce 500ms; requer 10+ dígitos antes de disparar.
 */
export function useWhatsAppCheck(phone, enabled = true) {
  const [state, setState] = useState({ status: "idle" });

  useEffect(() => {
    if (!enabled) {
      setState({ status: "idle" });
      return;
    }
    const digits = String(phone || "").replace(/\D/g, "");
    if (digits.length < 10) {
      setState({ status: "idle" });
      return;
    }
    setState((s) => (s.status === "loading" ? s : { status: "loading" }));
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/whatsapp/check-phone?tel=${encodeURIComponent(digits)}`);
        const d = r.data || {};
        if (!d.configured) setState({ status: "not_configured" });
        else if (d.has_whatsapp === true) setState({ status: "has", phone_intl: d.phone_intl });
        else if (d.has_whatsapp === false) setState({ status: "no", phone_intl: d.phone_intl });
        else setState({ status: "error", error: d.error });
      } catch {
        setState({ status: "error" });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [phone, enabled]);

  return state;
}

/**
 * WhatsAppFieldStatus — ícone inline + hint textual quando ausente.
 *
 * Uso típico ao lado do input de telefone:
 *   <div className="relative">
 *     <Input ... />
 *     <WhatsAppFieldStatus phone={f.cliente_telefone} className="absolute right-2 top-1/2 -translate-y-1/2" />
 *   </div>
 *
 * Ou como componente separado com `showHint` pra renderizar msg abaixo.
 */
export function WhatsAppFieldStatus({
  phone, enabled = true, className = "", testId, showHint = true,
}) {
  const state = useWhatsAppCheck(phone, enabled);
  if (state.status === "idle" || state.status === "not_configured") return null;

  const iconWrap = "inline-flex items-center justify-center";
  const iconClass = "w-4 h-4";
  let icon = null;
  let hint = null;
  let title = "";
  let dataStatus = state.status;

  if (state.status === "loading") {
    icon = <Loader2 className={`${iconClass} animate-spin text-slate-400`} />;
    title = "Validando WhatsApp…";
  } else if (state.status === "has") {
    icon = <CheckCircle2 className={`${iconClass} text-emerald-600`} />;
    title = "Tem WhatsApp — mensagem automática será enviada";
  } else if (state.status === "no") {
    icon = <XCircle className={`${iconClass} text-rose-600`} />;
    title = "Este número não possui WhatsApp";
    if (showHint) {
      hint = (
        <div className="mt-1 text-xs text-rose-700 flex items-start gap-1" data-testid={testId ? `${testId}-hint` : undefined}>
          <XCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Este número não possui WhatsApp. A mensagem automática não será enviada.</span>
        </div>
      );
    }
  } else {
    icon = <MessageCircleOff className={`${iconClass} text-slate-400`} />;
    title = "Não foi possível validar agora";
  }

  return (
    <>
      <span
        className={`${iconWrap} ${className}`}
        title={title}
        data-testid={testId}
        data-status={dataStatus}
      >
        {icon}
      </span>
      {hint}
    </>
  );
}
